'use client';

import { ReactNode } from 'react';
import { cn } from '@utils/cn';
import { PromptBarScrim } from './PromptBarScrim';

interface PromptBarShellProps {
  children: ReactNode;
  className?: string;
  /** Render in document flow instead of fixed at the viewport bottom. */
  inline?: boolean;
  /** Rendered below the bordered pill, inside the same width-constrained (and, when not
   *  inline, fixed-positioned) container. */
  belowSlot?: ReactNode;
}

/**
 * Bordered/shadowed pill that hosts the prompt bar contents. Defaults to fixed
 * positioning at the bottom of the viewport; pass `inline` to render in-flow.
 */
export const PromptBarShell = ({
  children, className, inline = false, belowSlot,
}: PromptBarShellProps) => {
  const inner = (
    <div className="relative mx-auto w-full max-w-2xl px-1 lg:px-0">
      <div
        className={cn(
          'flex w-full flex-col gap-2 rounded-2xl border border-input bg-surface px-3 py-2 shadow-lg backdrop-blur-sm lg:px-4 lg:py-2.5',
          className,
        )}
      >
        {children}
      </div>
      {belowSlot}
    </div>
  );

  if (inline) return inner;

  return (
    <>
      <PromptBarScrim />
      <div className="fixed inset-x-1 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 lg:inset-x-2 lg:bottom-8">
        {inner}
      </div>
    </>
  );
};
